import { useId, forwardRef, useRef, useImperativeHandle } from 'react';
import styles from './common.module.scss';
import { Link } from 'react-router';
import { UIText, UITextProps } from '../ui-text';
import Select from 'react-select';
import { blue, moonlight, red, redLight } from '../../styles';
import { UITextButton } from '../..';
import { UISelectDropDownZIndex } from '../z-index';
import { __UISelectOptionItem } from './common';

export type UISelectValue = {
    label: string;
    labelProps?: UITextProps;
    subLabel?: string;
    subLabelProps?: UITextProps;
    value: string;
    isDisabled?: boolean;
};

export type UISelectInputProps = {
    name?: string;
    label?: string;
    labelButton?: {
        text: string;
        link: string;
    };
    value?: UISelectValue;
    options?: UISelectValue[];
    placeholder?: string;
    description?: string;
    required?: boolean;
    closeMenuOnSelect?: boolean;
    clearable?: boolean;
    disabled?: boolean;
    errorText?: string;
    onChange?: (option?: UISelectValue) => void;
    style?: React.CSSProperties;
};

export type UISelectInputRef = {
    focus: () => void;
};

export const UISelectInput = forwardRef<UISelectInputRef, UISelectInputProps>(
    (props, ref) => {
        const {
            name,
            label,
            labelButton,
            value,
            options,
            placeholder,
            description = '',
            required = false,
            closeMenuOnSelect = true,
            clearable = true,
            disabled = false,
            errorText = '',
            onChange,
            style
        } = props;

        const id = useId();

        const inputRef = useRef<HTMLInputElement>(null);
        useImperativeHandle(ref, () => ({
            focus() {
                inputRef.current?.focus();
            }
        }));

        return (
            <div className={styles.container} style={style}>
                {label && (
                    <div className={styles.labelContainer}>
                        <div>
                            <label className={styles.label} htmlFor={id}>
                                {label}
                            </label>
                            {required ? (
                                <label
                                    className={`${styles.label} ${styles.labelRequired}`}
                                    htmlFor={id}
                                >
                                    {' *'}
                                </label>
                            ) : undefined}
                        </div>

                        {labelButton && (
                            <Link
                                to={labelButton.link}
                                style={{ textDecoration: 'none' }}
                            >
                                <UITextButton variant="primary">
                                    {labelButton.text}
                                </UITextButton>
                            </Link>
                        )}
                    </div>
                )}
                <Select
                    name={name}
                    value={value}
                    options={options}
                    closeMenuOnSelect={closeMenuOnSelect}
                    isClearable={clearable}
                    placeholder={placeholder}
                    menuPortalTarget={document.body}
                    menuShouldScrollIntoView={false}
                    isDisabled={disabled}
                    components={{
                        Option: (props) => (
                            <__UISelectOptionItem
                                label={props.data.label}
                                labelProps={props.data.labelProps}
                                subLabel={props.data.subLabel}
                                subLabelProps={props.data.subLabelProps}
                                selected={props.isSelected}
                                disabled={props.isDisabled}
                                onClick={() => props.selectOption(props.data)}
                            />
                        )
                    }}
                    styles={{
                        menuPortal: (base) => ({
                            ...base,
                            zIndex: UISelectDropDownZIndex
                        })
                    }}
                    onChange={(_selectedOption) => {
                        if (_selectedOption) {
                            const selectedOption = {
                                value: _selectedOption.value,
                                label: _selectedOption.label
                            };
                            onChange?.(selectedOption);
                        } else {
                            onChange?.(undefined);
                        }
                    }}
                    classNames={{
                        control: () => styles.reactSelectControl,
                        singleValue: () => styles.reactSelectSingleValue,
                        option: () => styles.reactSelectOption,
                        placeholder: () => styles.reactSelectPlaceholder,
                        menu: () => styles.reactSelectMenu
                    }}
                    theme={(theme) => ({
                        ...theme,
                        borderRadius: 5,
                        borderWidth: 1,
                        colors: {
                            ...theme.colors,
                            primary25: moonlight,
                            primary: blue,
                            danger: red,
                            dangerLight: redLight,
                            neutral10: moonlight
                        }
                    })}
                />
                {errorText && (
                    <div className={styles.errorTextContainer}>
                        <UIText variant="small" type="error">
                            {errorText}
                        </UIText>
                    </div>
                )}
                {!errorText && description && (
                    <div className={styles.descriptionTextContainer}>
                        <UIText variant="small" type="default2">
                            {description}
                        </UIText>
                    </div>
                )}
            </div>
        );
    }
);
